<template>
  <div>
    <b-card
      title="Payment Method Settings"
      class="custom-card-header"
    />
    <b-card-actions
      title="Filters"
      class="waybill-series-filter"
      action-collapse
      collapsed
    >
      <b-row>
        <b-col md="3">
          <b-form-group
            label="Name"
            label-for="name"
          >
            <b-form-input
              id="name"
              v-model="filters['name']"
              placeholder="Search"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Merchant ID"
            label-for="merchant_id"
          >
            <b-form-input
              id="merchant_id"
              v-model="filters['merchant_id']"
              placeholder="Search"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group
            label="Status"
            label-for="status"
          >
            <v-select
              id="status"
              v-model="filters['status']"
              :options="statusOptions"
              :reduce="option => option.status"
              label="name"
              autocomplete="nope"
              placeholder="Select Status"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <!-- Apply and Clear Filters -->
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            :filter-data="filters"
            :apply-filters="applyFilters"
            :clear-filters="clearFilters"
          />
        </b-col>
      </b-row>
    </b-card-actions>
    <b-card title="QR Payment Method Settings">
      <div>
        <b-row>
          <b-col
            cols="12"
            md="8"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0 mt-1"
          >
            <b-button
              v-can="'edit-payment-setting'"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              class="custom-button-color add-new-button"
              @click="onClickOpenAddPaymentGatewayModal"
            >
              <feather-icon icon="PlusIcon" />
              Add New
            </b-button>
          </b-col>
          <b-col
            class="d-flex align-items-center justify-content-end mb-1 mb-md-0  mt-1"
          >
            <ReportTableButtons
              :items="rows"
              :json_fieldz="json_fields"
              :name="`PaymentGateways - Page ${page}.xls`"
              :bulkname="`PaymentGateways.xls`"
              :fetch="getPaymentGatewayListNoPagination"
              @refresh="onClickRefresh"
            />
          </b-col>
        </b-row>
      </div>
      <b-overlay
        id="overlay-background"
        class="mt-2"
        blur="10px"
        opacity="0.30"
        rounded="sm"
        variant="light"
      >
        <b-table
          hover
          responsive
          striped
          show-empty
          :per-page="perPage"
          :items="rows"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          sticky-header="100vh"
          class="hide-vertical-scroll"
        >
          <template #empty>
            <TableDataFetching
              :rows="rows"
              :data-loading="dataLoading"
            />
          </template>
          <template #cell(status)="data">
            <div v-if="data.value === 'active'">
              <b-badge variant="light-success">
                Active
              </b-badge>
            </div>
            <div v-if="data.value === 'inactive'">
              <b-badge variant="light-danger">
                Inactive
              </b-badge>
            </div>
          </template>
          <template #cell(action)="data">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              :class="rows.length !== 1 ? 'dropdown-no-scroll':''"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="12"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item
                v-can="'edit-payment-setting'"
                @click="onClickEditPaymentGateway(data.item.id)"
              >
                <feather-icon icon="EditIcon" />
                Edit
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.status === 'active'"
                v-can="'edit-payment-setting'"
                @click="statusAction(data.item.id , 3)"
              >
                <feather-icon icon="ToolIcon" />
                Inactive
              </b-dropdown-item>
              <b-dropdown-item
                v-if="data.item.status === 'inactive'"
                v-can="'edit-payment-setting'"
                @click="statusAction(data.item.id , 2)"
              >
                <feather-icon icon="ToolIcon" />
                Active
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-overlay>
      <div>
        <b-row
          cols="12"
        >
          <b-col
            class="d-flex align-items-center justify-content-sm-start"
            md="4"
          >
            <label>Per page</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="pageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-1 width-100 mr-1"
            />
          </b-col>
          <b-col
            md="8"
            class="d-flex align-items-center justify-content-end"
          >
            <span class="text-muted mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
            <b-pagination
              v-model="page"
              :per-page="perPage"
              :total-rows="(meta.total)? meta.total : 0"
              class="mb-0 mt-1 mt-sm-0"
              first-number
              last-number
              next-class="next-item"
              prev-class="prev-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <add-payment-gateway ref="add_payment_gateway_modal" />
  </div>
</template>

<script>
import {
  BRow, BCol, BButton, BCard, BOverlay, BTable, BPagination, BDropdown, BDropdownItem, BFormGroup, BFormInput, BBadge,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import Filter from '@core/mixins/filter'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import AddPaymentGateway from '@/components/Payment/PaymentGatewayModal.vue'

const PaymentRepository = RepositoryFactory.get('payment')

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BOverlay,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    AddPaymentGateway,
    BCardActions,
    BFormGroup,
    BFormInput,
    FilterButtons,
    BBadge,
  },
  directives: {
    Ripple,
  },
  mixins: [SuccessMessage, ErrorMessage, Filter],
  data() {
    return {
      dataLoading: false,
      sort: '',
      meta: {},
      perPage: 20,
      pageOptions: [10, 20, 50],
      page: 1,
      fields: [
        {
          key: 'name', label: 'Gateway Name', sortable: true, thClass: ' text-left', tdClass: ' text-left',
        },
        {
          key: 'merchant_id', label: 'Merchant ID', sortable: true, thClass: ' text-left', tdClass: ' text-left',
        },
        {
          key: 'environment', label: 'Environment', sortable: true, thClass: ' text-left', tdClass: ' text-left',
        },
        {
          key: 'status', label: 'Status', sortable: false, thClass: ' text-left', tdClass: ' text-left',
        },
        {
          key: 'action', label: 'Action', sortable: false, thClass: ' text-center', tdClass: ' text-center',
        },
      ],
      json_fields: {
        'Gateway Name': {
          field: 'name',
          callback: value => `"${value}"`,
        },
        'Merchant ID': 'merchant_id',
        Environment: 'environment',
      },
      rows: [],
      filters: {},
      statusOptions: [
        { name: 'Active', status: 'active' },
        { name: 'Inactive', status: 'inactive' },
      ],
    }
  },
  watch: {
    page() {
      this.getPaymentGatewayList()
    },
    perPage() {
      this.getPaymentGatewayList()
    },
  },
  mounted() {
    this.getPaymentGatewayList()
  },
  methods: {
    async getPaymentGatewayList() {
      this.dataLoading = true
      this.rows = []
      try {
        // eslint-disable-next-line no-underscore-dangle
        this.filterQuery = this.__refineDateQuery(this.filterQuery, [this.filters])
        const { data } = (await PaymentRepository.paymentGateways(this.page, this.filterQuery, this.perPage))
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async getPaymentGatewayListNoPagination() {
      try {
        // eslint-disable-next-line no-underscore-dangle
        this.filterQuery = this.__refineDateQuery(this.filterQuery, [this.filters])
        const { data } = (await PaymentRepository.paymentGatewaysNoPagination(this.filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async statusAction(id, status) {
      try {
        const payload = {
          status,
        }
        const response = await PaymentRepository.updatePaymentGatewayStatus(id, payload)
        if (response.status === 200) {
          this.showSuccessMessage(response.data.message)
          this.getPaymentGatewayList()
        } else {
          this.showErrorMessage(response.data.message)
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    onClickOpenAddPaymentGatewayModal() {
      this.$refs.add_payment_gateway_modal.openModal(null, 'add')
    },
    onClickEditPaymentGateway(id) {
      this.$refs.add_payment_gateway_modal.openModal(id, 'edit')
    },
    applyFilters() {
      this.getPaymentGatewayList()
    },
    clearFilters() {
      this.filters = {}
      this.$nextTick(() => {
        this.getPaymentGatewayList()
      })
    },
    handleChangePage(page) {
      this.page = page
      this.getPaymentGatewayList()
    },
    changePage(value) {
      this.perPage = value
    },
    onClickRefresh() {
      this.getPaymentGatewayList()
    },
  },
}
</script>
<style scoped>
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
</style>
